import { z } from "zod";
import { AgentId, ConversationId, MessageId, ProjectId, TerminalId, Timestamp, UserId, Usage, SessionId, WakeId } from "./common.js";
import { Actor, Agent, AgentStatus, Approval, Conversation, FsChangeKind, FsPath, GuardReason, Message } from "./entities.js";
import { AssetId, HumanQuestion, MediaJob, Meeting, MeetingId, Notification, PullRequest, Task } from "./collab.js";
import { ApiErrorBody } from "./errors.js";

/**
 * 客户端实时 WS（doc/api_v1.md §2）：`/api/v1/ws`，一条连接多路订阅。
 * 每个通道的事件有单调递增 seq；重连时带 since 补发，窗口外返回 gap。
 */

export const Channel = z.union([
  z.templateLiteral(["user:", UserId]),
  z.templateLiteral(["project:", ProjectId]),
  z.templateLiteral(["agent:", AgentId, ":session"]),
  z.templateLiteral(["agent:", AgentId, ":fs"]),
  z.templateLiteral(["conversation:", ConversationId]),
  z.templateLiteral(["terminal:", TerminalId]),
  z.templateLiteral(["meeting:", MeetingId]),
]);
export type Channel = z.infer<typeof Channel>;

export const ClientFrame = z.discriminatedUnion("t", [
  /** id 可选：订阅被拒时服务端回 nack{id}（缺省 id = "sub:<ch>"） */
  z.object({ t: z.literal("sub"), ch: Channel, since: z.number().int().nonnegative().optional(), id: z.string().optional() }),
  z.object({ t: z.literal("unsub"), ch: Channel }),
  z.object({ t: z.literal("cmd"), id: z.string(), ch: Channel, op: z.string(), data: z.unknown() }),
  z.object({ t: z.literal("ping") }),
]);
export type ClientFrame = z.infer<typeof ClientFrame>;

const SessionRef = { sessionId: SessionId.optional(), sessionSeq: z.number().int().optional() };

/** 每种事件的 data 形状；新增事件只在这里加一项。 */
export const EventTypes = {
  // user:*
  /** 完整的 Notification（控制面总是带上 category / link / read / createdAt；旧客户端只读 id / code / params，仍兼容） */
  "notification.new": Notification.partial({ link: true, read: true, createdAt: true }),
  // project:*
  "agent.created": Agent,
  "agent.updated": Agent,
  "agent.status": z.object({ agentId: AgentId, status: AgentStatus }),
  "approval.requested": Approval,
  "approval.resolved": Approval,
  // agent:*:session —— 已落库的事件带 sessionId + sessionSeq（与 agentSessionEvents 回放的 seq 同源，用于去重）
  "run.started": z.object({ wakeId: WakeId, sessionId: SessionId, sessionSeq: z.number().int().optional(), mergedWakeIds: z.array(WakeId).optional() }),
  "sdk.message": z.object({ wakeId: WakeId, payload: z.unknown(), ...SessionRef }),
  "run.finished": z.object({ wakeId: WakeId, usage: Usage, stopReason: z.string(), ...SessionRef }),
  "run.interrupted": z.object({ wakeId: WakeId, ...SessionRef }),
  "run.error": z.object({ wakeId: WakeId.nullable(), code: z.string(), ...SessionRef }),
  // agent:*:fs
  "fs.changed": z.object({ path: FsPath, change: FsChangeKind, by: z.enum(["agent", "user", "system"]) }),
  // conversation:*
  "message.created": Message,
  "guard.paused": z.object({ reason: GuardReason }),
  // terminal:*（输出走二进制帧，这里只有退出）
  "terminal.exit": z.object({ code: z.number().int() }),
  // ── P2+（project:*）
  "task.created": Task,
  "task.updated": Task,
  "pr.updated": PullRequest,
  "mail.received": z.object({ agentId: AgentId, mailId: MessageId, from: z.string(), subject: z.string() }),
  /** 完整的 HumanQuestion（控制面总是带上 projectId / options / status / answer / createdAt；旧客户端只读 id / agentId / question / urgent） */
  "question.asked": HumanQuestion.partial({ projectId: true, options: true, status: true, answer: true, createdAt: true }),
  "question.answered": z.object({ id: z.string() }),
  "asset.ready": z.object({ jobId: z.string(), assetIds: z.array(AssetId) }),
  /** 媒体任务状态变化（queued → running → succeeded / failed），P4 */
  "media.job_updated": MediaJob,
  "usage.threshold": z.object({ scope: z.enum(["agent", "project", "tenant"]), id: z.string(), percent: z.number() }),
  // ── P3+（conversation:* / meeting:*）
  "message.updated": Message,
  "member.changed": z.object({ members: z.array(Actor) }),
  "typing": z.object({ agentId: AgentId }),
  "guard.resumed": z.object({ by: Actor }),
  /** 会话属性变化（暂停 / 恢复、预算与已用、标题、静音、成员），data 是完整的 Conversation */
  "conversation.updated": Conversation,
  "meeting.state": Meeting,
  "meeting.turn": z.object({ meetingId: MeetingId, speaker: AgentId, round: z.number().int() }),
} as const;

export type EventType = keyof typeof EventTypes;
export type EventData<T extends EventType> = z.infer<(typeof EventTypes)[T]>;

export const EventFrame = z.object({
  t: z.literal("evt"),
  ch: Channel,
  seq: z.number().int().nonnegative(),
  type: z.enum(Object.keys(EventTypes) as [EventType, ...EventType[]]),
  data: z.unknown(),
  at: Timestamp,
});
export type EventFrame = z.infer<typeof EventFrame>;

export const ServerFrame = z.discriminatedUnion("t", [
  EventFrame,
  z.object({ t: z.literal("ack"), id: z.string(), ok: z.literal(true), data: z.unknown().optional() }),
  z.object({ t: z.literal("nack"), id: z.string(), error: ApiErrorBody }),
  /** 订阅确认；terminal 通道附带二进制帧通道号 */
  z.object({ t: z.literal("subscribed"), ch: Channel, seq: z.number().int().nonnegative(), binaryChannel: z.number().int().min(0).max(255).optional() }),
  z.object({ t: z.literal("gap"), ch: Channel }),
  z.object({ t: z.literal("pong") }),
]);
export type ServerFrame = z.infer<typeof ServerFrame>;

/** WS cmd 的 op（按通道类型） */
export const TerminalCmd = z.discriminatedUnion("op", [
  z.object({ op: z.literal("input"), data: z.object({ text: z.string() }) }),
  z.object({ op: z.literal("resize"), data: z.object({ cols: z.number().int().positive(), rows: z.number().int().positive() }) }),
  z.object({ op: z.literal("close"), data: z.object({}).optional() }),
]);
export const SessionCmd = z.discriminatedUnion("op", [z.object({ op: z.literal("interrupt"), data: z.object({}).optional() })]);

/** 二进制帧：[1 字节 binaryChannel][payload] */
export function encodeBinaryFrame(channel: number, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(payload.length + 1);
  out[0] = channel;
  out.set(payload, 1);
  return out;
}
export function decodeBinaryFrame(frame: Uint8Array): { channel: number; payload: Uint8Array } {
  return { channel: frame[0] ?? 0, payload: frame.subarray(1) };
}
